import type { IParagraphNode } from "~/models/IParagraphNode";
import type {
  IParagraphEntry,
  ISchema,
  INote,
  ILinkChain,
} from "~/models/IUserEntry";

export function useEntrySpans(
  node: Ref<IParagraphNode & { entries?: IParagraphEntry[] }>,
  text: string
) {
  const spans = computed(() => {
    if (!node.value.entries?.length || !text) {
      return null;
    }

    const entriesByLetter = Array.from(text).map((letter) => ({
      letter,
      entries: [] as IParagraphEntry[],
      startsHere: [] as Extract<
        IParagraphEntry,
        ISchema | INote | ILinkChain
      >[],
    }));

    for (const entry of node.value.entries) {
      const position = entry.position;
      const startsHere = position.node_start_id === node.value.id;
      const start = startsHere ? position.word_start_offset : 0;
      const end =
        position.node_end_id === node.value.id
          ? position.word_end_offset
          : entriesByLetter.length;

      const withIcon =
        startsHere &&
        (entry.type === "schema" ||
          entry.type === "note" ||
          entry.type === "link_chain");

      // when user set entry at the end of the paragraph
      if (start === entriesByLetter.length) {
        entriesByLetter[start - 1].entries.push(entry);
        if (withIcon) {
          entriesByLetter[start - 1].startsHere.push(entry);
        }
      } else {
        const stop = start === end ? end + 1 : end;
        for (let i = start; i < stop; i++) {
          entriesByLetter[i].entries.push(entry);
          if (withIcon) {
            entriesByLetter[i].startsHere.push(entry);
          }
        }
      }
    }

    const spanObjects = [] as {
      offset: number;
      text: string;
      entries: IParagraphEntry[];
      id: number;
      startsHere: Extract<IParagraphEntry, ISchema | INote | ILinkChain>[];
    }[];

    let offset = 0;

    for (const i in entriesByLetter) {
      const { entries, letter, startsHere } = entriesByLetter[i];
      const latestSpan = spanObjects[spanObjects.length - 1];
      if (JSON.stringify(latestSpan?.entries) === JSON.stringify(entries)) {
        latestSpan.text += letter;
      } else {
        spanObjects.push({
          offset,
          entries,
          text: letter,
          id: Math.random(),
          startsHere,
        });
      }
      offset += 1;
    }

    return spanObjects;
  });

  return {
    spans,
  };
}
